import React, {useEffect, useRef, useState} from 'react';
import {NavLink, Redirect, Route, Switch, useHistory, useLocation, useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {AbGlyph} from '../brand/AbMark';
import {store} from '../../store/Store';
import {logout as sanctumLogout} from '../../libraries/Authentication';
import {getRecommendSettings} from '../../libraries/AdminApi';
import {APP_ORIGIN} from '../../configs/Hosts';
import LabPage from './LabPage';
import UsersPage from './UsersPage';
import SettingsPage from './SettingsPage';
import GenresPage from './GenresPage';
import CatalogPage from './CatalogPage';

/** One shared preview player for the whole admin. */
function useAudio() {
    const ref = useRef(null);
    const [playing, setPlaying] = useState(null);
    useEffect(() => {
        ref.current = new Audio();
        ref.current.onended = () => setPlaying(null);
        return () => ref.current.pause();
    }, []);
    const toggle = (id, url) => {
        const a = ref.current;
        if (playing === id) {
            a.pause();
            setPlaying(null);
            return;
        }
        a.src = url;
        a.play().then(() => setPlaying(id)).catch(() => setPlaying(null));
    };
    return {playing, toggle};
}

function LabRoute({base, audio}) {
    const {id} = useParams();
    const history = useHistory();
    const location = useLocation();
    const ids = (new URLSearchParams(location.search).get('ids') || '').split(',').map(Number).filter(Boolean);
    return (
        <LabPage
            songId={id ? Number(id) : null}
            ids={ids}
            audio={audio}
            onOpen={(songId) => history.push(`${base}/lab/${songId}`)}
            onColumns={(next) => history.replace(`${base}/lab/${id}?ids=${next.join(',')}`)}
            onFollow={() => history.replace(`${base}/lab/${id}`)}
        />
    );
}

const NAV = [
    {to: '/lab', icon: 'fa-flask', label: 'Song Lab'},
    {to: '/catalog', icon: 'fa-compact-disc', label: 'Catalog'},
    {to: '/settings', icon: 'fa-sliders', label: 'Settings'},
    {to: '/genres', icon: 'fa-tags', label: 'Genres'},
    {to: '/users', icon: 'fa-user-group', label: 'Users'},
];

/**
 * admin.abtunes.com (or /admin on localhost). Access is checked against the
 * API, so a stale local session can't show the admin to a non-admin.
 */
export default function AdminApp({base = ''}) {
    const me = useSelector((s) => s.user.currentUser);
    const [access, setAccess] = useState('checking');
    const audio = useAudio();

    useEffect(() => {
        getRecommendSettings()
            .then(() => setAccess('ok'))
            .catch((e) => setAccess(e.status === 401 ? 'login' : 'denied'));
    }, []);

    const logout = async () => {
        await sanctumLogout(store.getState().auth?.jwtToken);
        window.location = '/login';
    };

    if (access === 'login') {
        return <Redirect to="/login"/>;
    }
    if (access !== 'ok') {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-ambient-base text-center text-white">
                <AbGlyph size={40}/>
                {access === 'checking' ? <p className="text-white/50">Checking access…</p> : (
                    <>
                        <p className="text-[15px]">This area is for AbTunes admins.</p>
                        <div className="flex gap-3">
                            <a href={APP_ORIGIN} className="rounded-xl bg-white/10 px-4 py-2 text-[13px] font-semibold">Open the app</a>
                            <button type="button" onClick={logout} className="rounded-xl bg-apple-red px-4 py-2 text-[13px] font-bold">Log out</button>
                        </div>
                    </>
                )}
            </div>
        );
    }

    return (
        <div className="relative min-h-screen bg-ambient-base font-display text-white">
            <div className="pointer-events-none fixed inset-0" style={{
                backgroundImage:
                    'radial-gradient(52% 60% at 68% 8%, rgba(250,45,72,.14), transparent 65%),'
                    + 'radial-gradient(45% 55% at 12% 85%, rgba(255,158,68,.09), transparent 62%)',
            }}/>
            <div className="relative flex min-h-screen">
                <aside className="sticky top-0 flex h-screen w-[210px] flex-none flex-col border-r border-white/[0.06] bg-black/20 px-3 py-5 backdrop-blur-xl">
                    <div className="flex items-center gap-2.5 px-2">
                        <AbGlyph size={24}/>
                        <span className="text-[15px] font-bold tracking-tight">AbTunes</span>
                        <span className="rounded bg-white/10 px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wider text-white/60">Admin</span>
                    </div>
                    <nav className="mt-8 space-y-1">
                        {NAV.map((n) => (
                            <NavLink key={n.to} to={base + n.to}
                                     className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] font-semibold text-white/60 transition hover:bg-white/[0.06] hover:text-white"
                                     activeClassName="!bg-white/[0.10] !text-white">
                                <i className={'fa-solid w-4 text-center text-[13px] ' + n.icon}/>{n.label}
                            </NavLink>
                        ))}
                    </nav>
                    <div className="mt-auto space-y-1 border-t border-white/[0.06] pt-4">
                        <a href={APP_ORIGIN} className="flex items-center gap-3 rounded-xl px-3 py-2 text-[12.5px] text-white/50 hover:text-white">
                            <i className="fa-solid fa-arrow-up-right-from-square w-4 text-center text-[11px]"/>Open the app
                        </a>
                        <button type="button" onClick={logout} className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-[12.5px] text-white/50 hover:text-white">
                            <i className="fa-solid fa-right-from-bracket w-4 text-center text-[11px]"/>Log out
                        </button>
                        <div className="truncate px-3 pt-1 text-[11px] text-white/30">{me?.attributes?.email}</div>
                    </div>
                </aside>
                <main className="min-w-0 flex-1 px-6 py-7 lg:px-10">
                    <Switch>
                        <Route path={`${base}/lab/:id?`}><LabRoute base={base} audio={audio}/></Route>
                        <Route path={`${base}/settings`}><SettingsPage/></Route>
                        <Route path={`${base}/genres`}><GenresPage/></Route>
                        <Route path={`${base}/catalog`}><CatalogPage base={base} audio={audio}/></Route>
                        <Route path={`${base}/users`}><UsersPage me={me}/></Route>
                        <Route><Redirect to={`${base}/lab`}/></Route>
                    </Switch>
                </main>
            </div>
        </div>
    );
}
